import { mapPendingList, oldestFinalAt, r2, toNumber } from "@/components/finance-shell/financeLogic";

// Čista logika ekrana "Knjiženje narudžbi" (admin): lista neproknjiženih narudžbi, stanje jedne narudžbe i ishod knjiženja.
// Oblik odgovora je iz backend poruke 30.10.2026 (ticket #223889); polja koja poruka ne navodi čitaju se tolerantno
// (restoran i kurir mogu stići kao id, naziv ili objekt) dok se ne potvrdi stvaran odgovor (dev/api F5, F6).

/** Ishod knjiženja jedne narudžbe */
export const POST_STATUSES = ["posted", "skipped", "no_op", "failed"];

const textOrEmpty = (value) => (value === null || value === undefined ? "" : String(value));

/** GET /admin/ledger/missing: { data: [...], meta: { total, returned, truncated, value_sum } } */
export const mapMissingList = (body) => {
  const list = mapPendingList(body);
  const source = (body && body.data && !Array.isArray(body.data) && !body.meta ? body.data : body) || {};
  const meta = (!Array.isArray(source) && source.meta) || {};
  const total = meta.total !== undefined && meta.total !== null ? toNumber(meta.total) : list.rows.length;
  return {
    rows: list.rows,
    total,
    returned: meta.returned !== undefined && meta.returned !== null ? toNumber(meta.returned) : list.rows.length,
    truncated: Boolean(meta.truncated) || total > list.rows.length,
    valueSum: meta.value_sum !== undefined && meta.value_sum !== null ? toNumber(meta.value_sum) : list.valueSum,
  };
};

/** "2026-10-26 08:21:23" -> "2026-10-26T08:21:23": razmak umjesto T ne čita svaki preglednik, a bez zone je lokalno vrijeme */
const isoLocal = (value) => textOrEmpty(value).replace(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2})/, "$1T$2");

// Jedno knjiženje (potvrđeno dev/api F6, 31.10.2026): { transaction_id, created_at, created_by, entry_types: [...], lines: 9 }.
// Iznos i smjer ne stižu po knjiženju, samo tipovi stavki i njihov broj.
const mapCheckTransaction = (dto) => {
  const types = Array.isArray(dto.entry_types) ? dto.entry_types : dto.entry_type ? [dto.entry_type] : [];
  return {
    id: dto.transaction_id !== undefined ? dto.transaction_id : dto.id,
    entryTypes: types.map(textOrEmpty).filter(Boolean),
    createdBy: textOrEmpty(dto.created_by),
    lines: dto.lines === undefined || dto.lines === null || dto.lines === "" ? null : toNumber(dto.lines),
    date: isoLocal(dto.created_at || dto.date),
  };
};

/** GET /admin/ledger/orders/{id}: posted, can_post, reason, transactions */
export const mapOrderCheck = (body) => {
  const source = (body && body.data && !Array.isArray(body.data) ? body.data : body) || {};
  const transactions = Array.isArray(source.transactions) ? source.transactions.map(mapCheckTransaction) : [];
  return {
    posted: Boolean(source.posted),
    canPost: Boolean(source.can_post),
    reason: textOrEmpty(source.reason),
    transactions,
  };
};

/** Ishod jedne narudžbe iz POST odgovora: status posted (transaction_id) | skipped | no_op | failed (message). */
export const mapPostResult = (dto, fallbackOrderId = null) => {
  const wrapped = dto && dto.status === undefined && dto.data && typeof dto.data === "object" && !Array.isArray(dto.data);
  const source = (wrapped ? dto.data : dto) || {};
  const status = textOrEmpty(source.status).toLowerCase();
  return {
    orderId: source.order_id !== undefined && source.order_id !== null ? source.order_id : fallbackOrderId,
    status: POST_STATUSES.includes(status) ? status : status || "failed",
    transactionId: source.transaction_id === undefined ? null : source.transaction_id,
    message: textOrEmpty(source.message),
  };
};

/** Odgovor masovnog knjiženja: niz (results/data/orders), mapa po broju narudžbe ili jedan ishod; narudžba bez ishoda je neuspjela. */
export const mapBulkResults = (body, orderIds) => {
  const source = (body && body.data && !Array.isArray(body.data) && !body.results ? body.data : body) || {};
  let list = Array.isArray(source) ? source : source.results || source.data || source.orders || null;
  if (list && !Array.isArray(list) && typeof list === "object") {
    list = Object.keys(list).map((id) => ({ order_id: Number(id), ...list[id] }));
  }
  const byId = new Map();
  (list || []).forEach((dto) => {
    const result = mapPostResult(dto);
    if (result.orderId !== null) byId.set(String(result.orderId), result);
  });
  return orderIds.map((id) => byId.get(String(id)) || { orderId: id, status: "failed", transactionId: null, message: "" });
};

/** Zbirni ishod za poruku: koliko je po statusu. */
export const countResults = (results) =>
  results.reduce(
    (sum, result) => {
      sum[POST_STATUSES.includes(result.status) ? result.status : "failed"] += 1;
      return sum;
    },
    { posted: 0, skipped: 0, no_op: 0, failed: 0 }
  );

/** Filteri liste: ishod i način plaćanja (tekstualna pretraga je customSearch na ekranu). */
export const filterMissing = (rows, { outcome = "all", payment = "all" } = {}) =>
  rows.filter((row) => (outcome === "all" || row.outcome === outcome) && (payment === "all" || row.paymentMethod === payment));

/** Najstarije čeka prvo (final_at), pa po broju narudžbe; redovi bez datuma na kraju. */
export const sortMissing = (rows) =>
  [...rows].sort((a, b) => {
    if (a.finalAt !== b.finalAt) {
      if (!a.finalAt) return 1;
      if (!b.finalAt) return -1;
      return a.finalAt.localeCompare(b.finalAt);
    }
    return Number(a.orderId) - Number(b.orderId);
  });

export const sumValue = (rows) => r2(rows.reduce((sum, row) => sum + (row.value || 0), 0));

export { oldestFinalAt };
